import { calculateScale, isViewInLayerVisibilityRange } from './mapUtils.js';
import { refreshBootstrapTooltip, translate } from './languageTranslator.js';

const ADDRESS_AIDS_PC_LEVELS = ['PC6', 'NIS6', 'PC4'];
const ADDRESS_SELECTION_MAX_SCALE_DENOMINATOR = 8000;
const ADDRESS_AIDS_PC_LEVEL_ALIASES = {
  PC4: ['PC4', 'POSTCODE4', 'POSTALCODE4', 'CODEPOSTAL4'],
  NIS6: ['NIS6'],
  PC6: ['PC6', 'POSTCODE6', 'POSTALCODE6', 'CODEPOSTAL6']
};
const ADDRESS_LAYER_ALIASES = ['ADDRESS', 'ADDRESSES'];

function getPCLevelForLayer(layer) {
  const sourceName = String(layer?.get?.('typeName') || '').trim().toUpperCase();
  if (sourceName === 'GEOLINQ:NL_4PP') return 'PC4';
  if (sourceName === 'GEOLINQ:NL_6PP') return 'PC6';
  const typeName = String(layer?.get?.('typeName') || '').trim().toUpperCase();
  const title = String(layer?.get?.('title') || layer?.get?.('name') || '')
    .trim()
    .toUpperCase()
    .replace(/[\s_-]+/g, '');

  if (title === 'NIS6' || typeName === 'GEOLINQ:U1014136VIEWGROUPADRESTYPE1' || typeName === 'GEOLINQ:BE_DEELGEMEENTEN') {
    return 'NIS6';
  }

  if (
    title === 'POSTALCODE'
    || title === 'POSTALCODES'
    || typeName === 'GEOLINQ:U1014134VIEWGROUPADRESTYPE1'
    || typeName === 'GEOLINQ:BE_4PP'
  ) {
    return 'PC6';
  }

  if (title === 'NIS9') {
    return null;
  }

  const role = normalizeLayerKey(layer?.get?.('addressAidsRole')).toUpperCase();
  if (ADDRESS_AIDS_PC_LEVELS.includes(role)) {
    return role;
  }

  for (const level of ADDRESS_AIDS_PC_LEVELS) {
    if (ADDRESS_AIDS_PC_LEVEL_ALIASES[level].some(alias => title === alias)) {
      return level;
    }
  }

  return null;
}

function normalizeLayerKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/^layers\.base\./, '')
    .replace(/^geolinq:/, '')
    .replace(/\([^)]*\)/g, '')
    .replace(/[^a-z0-9]/g, '');
}

function normalizeLayerRole(value) {
  const normalized = normalizeLayerKey(value);
  if (normalized === 'addresses') return 'address';
  return normalized;
}

function isAddressLayer(layer) {
  const role = normalizeLayerRole(layer?.get?.('addressAidsRole'));
  if (role === 'address') return true;

  const title = normalizeLayerKey(layer?.get?.('title') || layer?.get?.('name'));
  return ADDRESS_LAYER_ALIASES.some(alias => title === normalizeLayerKey(alias));
}

function isLayerVisibleAtResolution(layer, resolution, zoom) {
  if (!layer?.getVisible?.()) return false;
  if (layer.get?.('disabledByRights')) return false;
  if (layer.get?.('visibleAtCurrentZoom') === true) return true;

  return isViewInLayerVisibilityRange(layer, resolution, zoom);
}

function collectVisiblePCLayers(layerCollection, resolution, zoom, parentVisible = true, visibleLevels = new Set()) {
  layerCollection?.forEach(layer => {
    const pcLevel = getPCLevelForLayer(layer);
    const layerVisible = parentVisible && isLayerVisibleAtResolution(layer, resolution, zoom);

    if (pcLevel && layerVisible) {
      visibleLevels.add(pcLevel);
    }

    if (typeof layer.getLayers === 'function') {
      collectVisiblePCLayers(layer.getLayers(), resolution, zoom, layerVisible, visibleLevels);
    }
  });

  return visibleLevels;
}

function collectVisibleAddressLayers(layerCollection, resolution, zoom, parentVisible = true, visibleLayers = new Set()) {
  layerCollection?.forEach(layer => {
    const layerVisible = parentVisible && isLayerVisibleAtResolution(layer, resolution, zoom);
    if (layerVisible && isAddressLayer(layer)) {
      visibleLayers.add(String(layer?.get?.('title') || layer?.get?.('name') || '').trim().toUpperCase());
    }

    if (typeof layer.getLayers === 'function') {
      collectVisibleAddressLayers(layer.getLayers(), resolution, zoom, layerVisible, visibleLayers);
    }
  });

  return visibleLayers;
}

export function getAddressAidsAvailability(map) {
  if (!map?.getView) {
    return {
      allowed: false,
      reason: 'Map is not ready.'
    };
  }

  const resolution = map.getView().getResolution();
  const zoom = map.getView().getZoom?.();
  const scale = calculateScale(map);
  const visibleLevels = collectVisiblePCLayers(map.getLayers(), resolution, zoom);
  const visibleAddressLayers = collectVisibleAddressLayers(map.getLayers(), resolution, zoom);
  const activeLevel = ADDRESS_AIDS_PC_LEVELS.find(level => visibleLevels.has(level));
  const activeAddressLayer = visibleAddressLayers.size ? Array.from(visibleAddressLayers)[0] : null;

  if (!Number.isFinite(scale) || scale > ADDRESS_SELECTION_MAX_SCALE_DENOMINATOR) {
    return {
      allowed: false,
      activeLevel,
      activeAddressLayer,
      reason: 'Zoom in to 1:8,000 or closer to use address selection.'
    };
  }

  if (!activeLevel && !activeAddressLayer) {
    return {
      allowed: false,
      activeLevel,
      activeAddressLayer,
      reason: 'Zoom until PC4, NIS6, PC6 or Addresses is visible to use address selection.'
    };
  }

  const contexts = [
    activeLevel,
    activeAddressLayer
  ].filter(Boolean);

  return {
    allowed: true,
    activeLevel,
    activeAddressLayer,
    reason: `Address selection available using ${contexts.join(', ')}.`
  };
}

let lastAvailabilityAllowed = null;

export function updateAddressAidsDrawButtons(map) {
  const availability = getAddressAidsAvailability(map);
  const buttons = document.querySelectorAll('.draw-polygon, .draw-circle');

  buttons.forEach(button => {
    button.disabled = !availability.allowed;
    button.classList.toggle('disabled', !availability.allowed);
    button.classList.toggle('address-aids-available', availability.allowed);
    button.classList.toggle('address-aids-blocked', !availability.allowed);
    button.setAttribute('aria-disabled', String(!availability.allowed));

    const translationKey = button.classList.contains('draw-circle')
      ? 'address_selection.draw_circle'
      : 'address_selection.draw_polygon';
    const title = availability.allowed ? translate(translationKey) : availability.reason;
    button.setAttribute('title', title);
    button.setAttribute('data-bs-title', title);
    button.setAttribute('data-bs-original-title', title);
    refreshBootstrapTooltip(button, title);
  });

  if (lastAvailabilityAllowed === true && !availability.allowed) {
    document.dispatchEvent(new CustomEvent('address-aids-unavailable', {
      detail: { reason: availability.reason }
    }));
  }

  lastAvailabilityAllowed = availability.allowed;

  return availability;
}

export function canUseAddressAidsDraw(map, notify = true) {
  const availability = updateAddressAidsDrawButtons(map);

  if (!availability.allowed && notify) {
    import('./mapUtils.js').then(MapUtils => {
      MapUtils.setStatus(availability.reason);
      MapUtils.setCommand('Address selection unavailable');
    });
  }

  return availability.allowed;
}
